import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';

/**
 * Compact key-value editor for the `attributes` map on a product.
 * Attributes power the dynamic filter sidebar on Category / Sale / Brand pages —
 * any key we save here becomes an automatic filter group across the site.
 *
 * Example keys: voltage, power_source, colour, material, size_category
 */
const AttributesEditor = ({ product, updateProduct }) => {
  const attrs = product.attributes || {};
  const entries = Object.entries(attrs);
  const [newKey, setNewKey] = useState('');
  const [newVal, setNewVal] = useState('');

  const save = async (next) => {
    await updateProduct(product.id, { attributes: next });
  };

  const setValue = async (key, value) => {
    const next = { ...attrs, [key]: value };
    await save(next);
  };

  const rename = async (oldKey, newKeyRaw) => {
    const key = (newKeyRaw || '').trim().toLowerCase().replace(/\s+/g, '_');
    if (!key || key === oldKey) return;
    const next = { ...attrs };
    next[key] = next[oldKey];
    delete next[oldKey];
    await save(next);
  };

  const remove = async (key) => {
    const next = { ...attrs };
    delete next[key];
    await save(next);
  };

  const add = async () => {
    const key = (newKey || '').trim().toLowerCase().replace(/\s+/g, '_');
    const val = (newVal || '').trim();
    if (!key || !val) return;
    const next = { ...attrs, [key]: val };
    await save(next);
    setNewKey(''); setNewVal('');
  };

  return (
    <div className="border-t border-neutral-200 pt-2 mt-2" data-testid={`attributes-editor-${product.id}`}>
      <div className="text-[11px] uppercase text-[#F46A25] font-bold mb-1">Attributes (voltage, power source, colour, …)</div>
      <p className="text-[10px] text-neutral-500 mb-2 leading-snug">
        Any key you add here becomes an automatic filter on category / brand / sale pages. Use lowercase, e.g. <code>voltage</code> = <code>18V</code>.
      </p>
      <div className="space-y-1">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center gap-1 text-xs" data-testid={`attr-row-${k}`}>
            <input
              defaultValue={k}
              onBlur={(e) => rename(k, e.target.value)}
              className="w-1/3 border border-neutral-200 rounded-sm px-1.5 py-1 font-mono uppercase text-[10px]"
              data-testid={`attr-key-${k}`}
            />
            <input
              defaultValue={v}
              onBlur={(e) => setValue(k, e.target.value)}
              className="flex-1 border border-neutral-200 rounded-sm px-2 py-1"
              data-testid={`attr-value-${k}`}
            />
            <button type="button" onClick={() => remove(k)} title="Remove" className="text-neutral-400 hover:text-red-600 p-1" data-testid={`attr-remove-${k}`}>
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <div className="flex items-center gap-1 text-xs pt-1">
          <input
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder="voltage"
            className="w-1/3 border border-dashed border-neutral-300 rounded-sm px-1.5 py-1 font-mono uppercase text-[10px]"
            data-testid={`attr-new-key-${product.id}`}
          />
          <input
            value={newVal}
            onChange={(e) => setNewVal(e.target.value)}
            placeholder="18V"
            className="flex-1 border border-dashed border-neutral-300 rounded-sm px-2 py-1"
            data-testid={`attr-new-value-${product.id}`}
          />
          <button type="button" onClick={add} className="text-[#F46A25] hover:text-[#12263A] p-1" data-testid={`attr-add-${product.id}`}>
            <Plus className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttributesEditor;
